import {accessItemTypeEnum, checkboxStateEnum} from '../../../serverClientShared/sharedEnums';

const {ITEM} = accessItemTypeEnum;
const {EMPTY, CHECKED, INDETERMINATE, INCLUDE_ALL} = checkboxStateEnum;


const getAncestorIds = (nodeId, nodeInfoMap) => {
    const route = nodeInfoMap.get(nodeId)?.nestedRoute || '';
    const parts = route.split('/').filter(Boolean);
    return parts.filter(id => id !== nodeId);
};

const isEffectiveIncludeAll = (id, {originalIncludeAll, addedIncludeAll, addedIndividual, removed}) => {
    if (addedIncludeAll.has(id)) return true;
    if (originalIncludeAll.has(id) && !removed.has(id) && !addedIndividual.has(id)) return true;
    return false;
};


const hasAccessDescendant = (nodeId, state, nodeInfoMap) => {
    const route = nodeInfoMap.get(nodeId)?.nestedRoute;
    if (!route) return false;
    const prefix = route + '/';
    const allWithAccess = [
        ...state.originalIncludeAll, ...state.originalNotIncludeAll,
        ...state.addedIncludeAll, ...state.addedIndividual,
    ];
    for (const id of allWithAccess) {
        if (id === nodeId || state.removed.has(id)) continue;
        const childRoute = nodeInfoMap.get(id)?.nestedRoute;
        if (childRoute && (childRoute.startsWith(prefix) || childRoute === route)) return true;
    }
    return false;
};

const getNodeSelfState = (nodeId, state, nodeInfoMap) => {
    const isItem = nodeInfoMap.get(nodeId)?.type === ITEM;
    if (state.removed.has(nodeId)) return EMPTY;
    if (state.addedIncludeAll.has(nodeId)) return CHECKED;
    if (state.addedIndividual.has(nodeId)) return isItem ? CHECKED : INDETERMINATE;
    return null;
};

const getAncestorCoverage = (nodeId, state, nodeInfoMap) => {
    const ancestors = getAncestorIds(nodeId, nodeInfoMap);
    for (let i = ancestors.length - 1; i >= 0; i--) {
        const id = ancestors[i];
        if (state.removed.has(id)) return EMPTY;
        if (isEffectiveIncludeAll(id, state)) return INCLUDE_ALL;
    }
    return null;
};

const getOriginalSelfState = (nodeId, state, nodeInfoMap) => {
    const isItem = nodeInfoMap.get(nodeId)?.type === ITEM;
    if (state.originalIncludeAll.has(nodeId)) return CHECKED;
    if (state.originalNotIncludeAll.has(nodeId)) return isItem ? CHECKED : INDETERMINATE;
    return null;
};

export const getCheckboxState = (nodeId, state, nodeInfoMap) => {
    const selfState = getNodeSelfState(nodeId, state, nodeInfoMap);
    if (selfState) return selfState;

    const coverage = getAncestorCoverage(nodeId, state, nodeInfoMap);
    if (coverage === INCLUDE_ALL) return CHECKED;

    if (coverage !== EMPTY) {
        const originalState = getOriginalSelfState(nodeId, state, nodeInfoMap);
        if (originalState) return originalState;
    }

    const isItem = nodeInfoMap.get(nodeId)?.type === ITEM;
    if (!isItem && hasAccessDescendant(nodeId, state, nodeInfoMap)) return INDETERMINATE;

    return EMPTY;
};


const ensureParentsAdded = (nodeId, state, nodeInfoMap) => {
    const ancestors = getAncestorIds(nodeId, nodeInfoMap);
    for (const id of ancestors) {
        state.removed.delete(id);
        state.addedIndividual.add(id);
    }
};

const clearDescendantsFromEdits = (nodeId, state, nodeInfoMap) => {
    const route = nodeInfoMap.get(nodeId)?.nestedRoute;
    if (!route) return;
    const prefix = route + '/';
    const isDescendant = (id) => {
        const r = nodeInfoMap.get(id)?.nestedRoute;
        return r && (r.startsWith(prefix) || r === route) && id !== nodeId;
    };
    for (const id of [...state.addedIncludeAll]) { if (isDescendant(id)) state.addedIncludeAll.delete(id); }
    for (const id of [...state.addedIndividual]) { if (isDescendant(id)) state.addedIndividual.delete(id); }
    for (const id of [...state.removed]) { if (isDescendant(id)) state.removed.delete(id); }
};

const downgradeIncludeAllAncestors = (nodeId, state, nodeInfoMap, childrenByParent) => {
    const ancestors = getAncestorIds(nodeId, nodeInfoMap);
    let branchChildId = nodeId;

    for (let i = ancestors.length - 1; i >= 0; i--) {
        const ancestorId = ancestors[i];
        if (!isEffectiveIncludeAll(ancestorId, state)) continue;

        state.addedIncludeAll.delete(ancestorId);
        state.addedIndividual.add(ancestorId);

        const siblings = (childrenByParent.get(ancestorId) || []);
        for (const siblingId of siblings) {
            if (siblingId === branchChildId) continue;
            const info = nodeInfoMap.get(siblingId);
            if (info?.type === ITEM) {
                state.addedIndividual.add(siblingId);
            } else {
                state.addedIncludeAll.add(siblingId);
            }
        }

        branchChildId = ancestorId;
    }
};

const toggleItemAdd = (nodeId, state, nodeInfoMap) => {
    state.removed.delete(nodeId);
    state.addedIndividual.add(nodeId);
    ensureParentsAdded(nodeId, state, nodeInfoMap);
};

const toggleItemRemove = (nodeId, state, nodeInfoMap, childrenByParent, isBulkMode) => {
    if (state.addedIndividual.has(nodeId)) {
        state.addedIndividual.delete(nodeId);
        if (!isBulkMode) return;
    }
    downgradeIncludeAllAncestors(nodeId, state, nodeInfoMap, childrenByParent);
    state.removed.add(nodeId);
};

const toggleContainerAdd = (nodeId, state, nodeInfoMap) => {
    state.removed.delete(nodeId);
    state.addedIndividual.delete(nodeId);
    state.addedIncludeAll.add(nodeId);
    clearDescendantsFromEdits(nodeId, state, nodeInfoMap);
    ensureParentsAdded(nodeId, state, nodeInfoMap);
};

const toggleContainerRemove = (nodeId, state, nodeInfoMap, childrenByParent) => {
    state.addedIncludeAll.delete(nodeId);
    state.addedIndividual.delete(nodeId);
    downgradeIncludeAllAncestors(nodeId, state, nodeInfoMap, childrenByParent);
    state.removed.add(nodeId);
    clearDescendantsFromEdits(nodeId, state, nodeInfoMap);
};

export const updateCheckbox = (nodeId, currentState, state, nodeInfoMap, childrenByParent, isBulkMode) => {
    const nodeType = nodeInfoMap.get(nodeId)?.type;

    if (nodeType === ITEM) {
        if (currentState === EMPTY) {
            toggleItemAdd(nodeId, state, nodeInfoMap);
        } else {
            toggleItemRemove(nodeId, state, nodeInfoMap, childrenByParent, isBulkMode);
        }
    } else {
        if (currentState === EMPTY || currentState === INDETERMINATE) {
            toggleContainerAdd(nodeId, state, nodeInfoMap);
        } else {
            toggleContainerRemove(nodeId, state, nodeInfoMap, childrenByParent);
        }
    }
};
